import { ShoppingCart } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/ui/core";

export interface CartButtonProps {
  /** Cuántos artículos hay en el carrito. 0 esconde el contador. */
  count?: number;
  onClick?: () => void;
}

/**
 * El carrito de la barra superior, con su contador.
 *
 * El número se pinta en un `<span aria-hidden>` y la cuenta REAL viaja en
 * el `aria-label` del botón ("Carrito (3 artículos)"): un lector de
 * pantalla leería "carrito 3" sin saber qué es ese 3.
 *
 * A partir de 100 se muestra "99+" — tres dígitos deforman la píldora y el
 * número exacto no le sirve a nadie en esa cifra.
 */
export function CartButton({ count = 0, onClick }: CartButtonProps) {
  const { t } = useTranslation();
  const hasItems = count > 0;

  return (
    <span className="relative inline-flex shrink-0">
      <Button
        variant="ghost"
        size="sm"
        iconOnly
        onClick={onClick}
        leftIcon={<ShoppingCart />}
        aria-label={
          hasItems ? t("Carrito ({{n}} artículos)", { n: count }) : t("Carrito vacío")
        }
        className="bg-white"
      />

      {hasItems && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute -right-1 -top-1 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-ds-pill bg-ds-violet-pop px-1 text-[10px] font-bold leading-none text-white shadow-ds-card"
        >
          {count > 99 ? "99+" : count}
        </span>
      )}
    </span>
  );
}
